import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { adminLlmBudget } from "@/lib/api";
import { Link } from "react-router-dom";

/**
 * Sticky top-of-page banner that shows only when the current-month LLM
 * spend has crossed the warn threshold (default 80%) or the cap (100%).
 * Silently ignores API errors so a Viewer or a network hiccup never
 * dumps a red banner on unrelated staff.
 *
 * Polls every 5 minutes — cheap read, matches the budget check job cadence.
 */
export default function BudgetBanner() {
  const [budget, setBudget] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await adminLlmBudget();
        if (!cancelled) setBudget(r);
      } catch { /* Viewer / unauth — ignore */ }
    };
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  if (!budget || budget.status === "ok") return null;

  const over = budget.status === "over";
  const tint = over ? "bg-red-50 border-red-300 text-red-900" : "bg-amber-50 border-amber-300 text-amber-900";
  const icon = over ? "text-red-600" : "text-amber-600";
  const label = over ? "AI budget exceeded" : `AI budget ${Math.round(budget.pct_used)}% used`;

  return (
    <div
      data-testid="budget-banner"
      data-status={budget.status}
      className={`border-b ${tint} px-6 py-2.5 flex items-center gap-3 text-sm`}
    >
      <AlertTriangle size={16} className={icon}/>
      <div className="flex-1 min-w-0">
        <span className="font-semibold" data-testid="budget-banner-label">{label}</span>
        <span className="opacity-70 mx-2">·</span>
        <span data-testid="budget-banner-spend">
          ${budget.month_to_date_usd.toFixed(2)} of ${budget.monthly_cap_usd.toFixed(2)}
        </span>
        <span className="opacity-70 mx-2">·</span>
        <span className="opacity-70">{budget.month_key}</span>
      </div>
      <Link
        to="/admin"
        className="text-xs uppercase tracking-wider underline underline-offset-2 hover:no-underline"
        data-testid="budget-banner-cta"
      >
        Manage budget →
      </Link>
    </div>
  );
}
